import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchAdminMe, fetchCinetpayStatus, fetchAdminPayments, revokeProSubscription } from "@/lib/api";
import { Wallet, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, RefreshCw, Copy, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { RefundsSection } from "@/components/RefundsSection";

const fmt = (n) => (typeof n === "number" ? n.toLocaleString("fr-FR") : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const MODE_STYLE = {
  production: "bg-emerald-50 text-emerald-800 border-emerald-300",
  sandbox: "bg-amber-50 text-amber-800 border-amber-300",
  dryrun: "bg-gray-100 text-gray-700 border-gray-300",
  disabled: "bg-red-50 text-red-800 border-red-300",
};
const STATUS_STYLE = {
  paid: "bg-emerald-50 text-emerald-700",
  paid_dryrun: "bg-gray-100 text-gray-600",
  revoked_dryrun: "bg-gray-100 text-gray-500 line-through",
  pending: "bg-amber-50 text-amber-700",
  failed: "bg-red-50 text-red-700",
  init_failed: "bg-red-50 text-red-700",
};

export default function AdminPaymentsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  const load = async (f = filter) => {
    setRefreshing(true);
    try {
      const [s, p] = await Promise.all([fetchCinetpayStatus(), fetchAdminPayments(f || undefined)]);
      setStatus(s);
      setPayments(p);
    } catch {
      toast.error("Erreur de chargement des paiements");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchAdminMe().then(() => load()).catch(() => navigate("/", { replace: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  if (!status || !payments) {
    return <main className="max-w-7xl mx-auto px-6 py-20 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" /></main>;
  }

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="admin-payments-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-orange-600 font-semibold mb-4" data-testid="back-to-admin">
        <ArrowLeft size={14} /> Tableau de bord
      </Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <h1 className="font-display text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
          <Wallet size={32} className="text-emerald-600" /> Paiements CinetPay
        </h1>
        <button onClick={() => load()} disabled={refreshing} className="btn-secondary inline-flex items-center gap-2" data-testid="payments-refresh-btn">
          <RefreshCw size={14} className={refreshing ? "animate-spin" : ""} /> Actualiser
        </button>
      </div>

      <CinetpayStatusCard status={status} />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <Stat label="Transactions" value={fmt(payments.total)} testid="stat-total" />
        <Stat label="Payées" value={fmt(payments.paid_count)} testid="stat-paid" />
        <Stat label="Encaissé" value={`${fmt(payments.paid_amount)} FCFA`} testid="stat-amount" accent />
      </div>

      <RevokeProCard onDone={() => load()} />

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <label className="!mb-0">Filtrer :</label>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); load(e.target.value); }} className="!w-auto" data-testid="payments-filter">
          <option value="">Tous</option>
          <option value="paid">Payées</option>
          <option value="pending">En attente</option>
          <option value="failed">Échouées</option>
          <option value="init_failed">Init. échouée</option>
          <option value="paid_dryrun">Test (dryrun)</option>
        </select>
      </div>

      {payments.items.length === 0 ? (
        <p className="text-sm text-[var(--ink-soft)] mt-8" data-testid="payments-empty">Aucune transaction.</p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="payments-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[var(--ink-soft)] border-b border-[var(--line)]">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">Transaction</th>
                <th className="py-2 pr-3">Utilisateur</th>
                <th className="py-2 pr-3">Plan</th>
                <th className="py-2 pr-3">Montant</th>
                <th className="py-2 pr-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {payments.items.map((p) => (
                <tr key={p.transaction_id} className="border-b border-[var(--line)]/60" data-testid={`payment-row-${p.transaction_id}`}>
                  <td className="py-2 pr-3 whitespace-nowrap">{fmtDate(p.paid_at || p.created_at)}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.transaction_id}</td>
                  <td className="py-2 pr-3">
                    <div className="font-semibold">{p.user_name || "—"}</div>
                    <div className="text-xs text-[var(--ink-soft)]">{p.user_email || p.user_phone || p.user_id}</div>
                  </td>
                  <td className="py-2 pr-3">{p.plan}</td>
                  <td className="py-2 pr-3 font-bold whitespace-nowrap">{fmt(p.amount)} {p.currency}</td>
                  <td className="py-2 pr-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_STYLE[p.status] || "bg-gray-100"}`}>{p.status}</span>
                    {p.error && <div className="text-[11px] text-red-600 mt-1 max-w-xs">{p.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RefundsSection />
    </main>
  );
}

function RevokeProCard({ onDone }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!window.confirm(`Retirer l'abonnement Pro de ${email.trim()} ?`)) return;
    setBusy(true);
    try {
      const r = await revokeProSubscription(email.trim());
      toast.success(`Pro retiré pour ${r.email}`);
      setEmail("");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la révocation");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="glass-card p-4 mt-6 flex items-end gap-3 flex-wrap" data-testid="revoke-pro-card">
      <div className="flex-1 min-w-[240px]">
        <label>Retirer le Pro d&apos;un compte (email)</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="utilisateur@exemple.com" data-testid="revoke-pro-email" />
      </div>
      <button type="submit" disabled={busy || !email} className="btn-secondary inline-flex items-center gap-2 !border-red-300 !text-red-700 disabled:opacity-50" data-testid="revoke-pro-btn">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <UserMinus size={14} />} Retirer Pro
      </button>
    </form>
  );
}

function Stat({ label, value, testid, accent }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-[var(--ink-soft)]">{label}</div>
      <div className={`text-2xl font-extrabold mt-1 ${accent ? "text-emerald-700" : ""}`}>{value}</div>
    </div>
  );
}

function CinetpayStatusCard({ status }) {
  const copyIp = () => {
    if (!status.outbound_ip) return;
    navigator.clipboard?.writeText(status.outbound_ip);
    toast.success("IP copiée");
  };
  return (
    <div className="glass-card p-5 mt-6" data-testid="cinetpay-status-card">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase border ${MODE_STYLE[status.mode] || ""}`} data-testid="cinetpay-mode">
          Mode {status.mode}
        </span>
        {status.oauth_ok ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm" data-testid="cinetpay-oauth-ok">
            <CheckCircle2 size={16} /> Connexion CinetPay OK
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-700 font-semibold text-sm" data-testid="cinetpay-oauth-ko">
            <AlertTriangle size={16} /> {status.error || "Connexion impossible"}
          </span>
        )}
      </div>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
        <Row k="API" v={status.base_url} />
        <Row k="Clé" v={status.key_prefix ? `${status.key_prefix} (${status.key_len} car.)` : "—"} />
        <Row k="Mot de passe API" v={status.password_len ? `${status.password_len} car. · empreinte ${status.password_fingerprint}` : "ABSENT"} testid="cinetpay-password-hint" />
        <Row k="Webhook (notify)" v={status.notify_url} />
        <Row k="Retour client" v={status.return_url} />
        <div className="flex items-center gap-2 md:col-span-2">
          <dt className="text-[var(--ink-soft)] whitespace-nowrap">IP sortante serveur :</dt>
          <dd className="font-mono font-bold" data-testid="cinetpay-outbound-ip">{status.outbound_ip || "—"}</dd>
          {status.outbound_ip && (
            <button onClick={copyIp} className="text-orange-600 inline-flex items-center gap-1 text-xs font-semibold" data-testid="copy-ip-btn">
              <Copy size={12} /> Copier
            </button>
          )}
        </div>
      </dl>
      {status.mode === "disabled" && (
        <div className="mt-3 text-xs bg-red-50 border border-red-200 rounded-xl p-3 text-red-800" data-testid="cinetpay-missing-vars">
          <b>Variables manquantes sur ce serveur :</b> {(status.missing || []).join(", ")}.<br />
          Ajoutez-les dans <b>Gérer les déploiements → Secrets</b> (noms exacts : <code>CINETPAY_API_KEY</code>, <code>CINETPAY_API_PASSWORD</code>, <code>CINETPAY_DRYRUN=0</code>, <code>APP_URL=https://fodindin.net</code>), puis redéployez. Tant que c&apos;est le cas, le bouton « Passer Pro » renvoie « Paiement momentanément indisponible ».
        </div>
      )}
      {!status.oauth_ok && status.mode === "production" && (
        <p className="text-xs text-[var(--ink-soft)] mt-3">
          Dans le back-office CinetPay, ajoutez l&apos;IP ci-dessus dans <b>Intégration → Adresses IP autorisées</b>, puis cliquez sur Actualiser.
        </p>
      )}
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex gap-2 min-w-0">
      <dt className="text-[var(--ink-soft)] whitespace-nowrap">{k} :</dt>
      <dd className="font-medium truncate" title={v}>{v}</dd>
    </div>
  );
}
